import React, { useRef, useEffect } from 'react';
import { styled, useTheme } from '@mui/styles';
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery
} from '@mui/material';
import LocalisedDisplayDate from '../../../../../Components/Locale/LocalisedDisplayDate.js';
import ArrowIcon from '@mui/icons-material/ArrowForward.js';
import LoadMoreButton from '../../../../../Components/LoadMoreButton.js';
import { isNearListBottom } from '../../../../../helpers/articleFeed.js';

const ArticlesBox = styled(Box)(({ theme }) => ({
  overflow: 'auto',
  height: 500,
  marginTop: 20,
  [theme.breakpoints.down('sm')]: {
    height: 'auto'
  }
}));

const ArticleBox = styled(Box)(({ theme, selected }) => ({
  position: 'relative',
  borderBottom: '1px solid #BFBFBF',
  backgroundColor: selected ? theme.palette.common.oatmeal : theme.palette.background.default,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '3rem 1rem',
  minHeight: 66,
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.common.oatmeal
  }
}));

const IconBox = styled(Box)({
  position: 'absolute',
  right: 10,
  top: 38
});

// Matches the all-products feed's scroll spinner: a centred, fixed-height slot at the
// list's bottom showing a CircularProgress only while the next chunk is in flight.
const ScrollLoaderBox = styled(Box)({
  textAlign: 'center',
  height: '4rem',
  paddingTop: '1.75rem'
});

export default function ArticleList ({ availableArticles, selectedArticleId, onArticleClick, onLoadMore, hasMore, loadingMore }) {
  const selectedRef = useRef(null);
  const theme = useTheme();
  // The list only scrolls on >= sm (fixed-height overflow box). On portrait mobile it's
  // height:auto inside the non-scrollable "see more articles" popin, so there the button is
  // the only pager; the scrollable surface gets the spinner instead.
  const isMobilePopin = useMediaQuery(theme.breakpoints.down('sm'));

  // On a deep-link the selected article is the OLDEST loaded (bottom of the list), so bring
  // it into view. block:'nearest' means no scroll when it's already visible (normal clicks).
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedArticleId]);

  // infinite scroll: when the paid feed nears its bottom, ask the parent for the
  // next chunk. The free path passes no onLoadMore, so it never paginates.
  const handleScroll = (e) => {
    if (onLoadMore && hasMore && isNearListBottom(e.currentTarget)) {
      onLoadMore();
    }
  };

  return (
    <ArticlesBox onScroll={handleScroll}>
      {
        availableArticles && availableArticles.map(article => {
          const isSelected = selectedArticleId === article.publicId;
          return (
            <ArticleBox
              key={article.id}
              data-testid='article-list-item'
              ref={isSelected ? selectedRef : null}
              selected={isSelected}
              onClick={() => onArticleClick(article)}
            >
              <Typography variant='h4' sx={{ width: '85%' }}>
                {article.title}
              </Typography>
              <Typography variant='body3'>
                <LocalisedDisplayDate
                  date={article.publishDateTime}
                />
              </Typography>
              {
                isSelected
                  ? (
                    <IconBox>
                      <ArrowIcon />
                    </IconBox>
                    )
                  : null
              }
            </ArticleBox>
          );
        })
      }
      {
        hasMore && onLoadMore && (
          isMobilePopin
            // Non-scrollable popin (portrait mobile): the button is the only pager.
            ? <LoadMoreButton loadMore={onLoadMore} loading={loadingMore} />
            // Scrollable surface: infinite scroll fetches the next chunk; show only a
            // spinner while it's in flight (matches the all-products feed).
            : (
              <ScrollLoaderBox>
                {loadingMore && <CircularProgress size={24} />}
              </ScrollLoaderBox>
              )
        )
      }
    </ArticlesBox>
  );
}
